import { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
    ArrowLeftOutlined,
    ArrowRightOutlined,
    CloseOutlined,
} from '@ant-design/icons';
import type { PopoverContentProps } from '@reactour/tour';
import { Flex, Typography } from 'antd';

import type { TourStepConfig } from '@/shared/types';
import { Button } from '@/shared/ui/button';

import cls from './tour-step.module.css';

export const TourStep = memo((props: PopoverContentProps) => {
    const { currentStep, steps, setIsOpen, setCurrentStep } = props;
    const { t } = useTranslation('tour');

    const isFirstStep = currentStep === 0;
    const isLastStep = currentStep === steps.length - 1;

    const currentStepData = steps[currentStep] as TourStepConfig | undefined;
    const { action, content } = currentStepData || {};

    useEffect(() => {
        if (action) {
            action();
        }
    }, [action]);

    const handleBack = () => {
        setCurrentStep((s) => s - 1);
    };

    const handleNext = () => {
        setCurrentStep((s) => s + 1);
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    if (!currentStepData) return null;

    return (
        <div className={cls.root}>
            <Button
                className={cls.closeButton}
                icon={<CloseOutlined />}
                onClick={handleClose}
                variant="ghost"
            />

            <div className={cls.stepCounter}>
                {currentStep + 1} / {steps.length}{' '}
                <span className={cls.stepLabel}>{t('common.steps')}</span>
            </div>

            <Typography.Paragraph>
                {typeof content === 'string' && content}
            </Typography.Paragraph>

            <Flex
                gap={16}
                justify={isFirstStep ? 'flex-end' : 'space-between'}
            >
                {!isFirstStep && (
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={handleBack}
                        title={t('common.back')}
                        variant="tertiary"
                    />
                )}
                {isLastStep ? (
                    <Button
                        onClick={handleClose}
                        title={t('common.close')}
                    />
                ) : (
                    <Button
                        icon={<ArrowRightOutlined />}
                        iconPosition="end"
                        onClick={handleNext}
                        title={t('common.next')}
                    />
                )}
            </Flex>
        </div>
    );
});

TourStep.displayName = 'TourStep';
